<script>
  // Prefix for the ids that tie each field to its visible label.
  const fid = `f${Math.random().toString(36).slice(2, 8)}`;
  import Layout from '../components/Layout.svelte';
  import CustomerAutocomplete from '../components/CustomerAutocomplete.svelte';
  import { companyAutofattureApi, companyInvoicesApi } from '../stores/api.js';
  import { activeCompanyId, activeCompany } from '../stores/company.js';
  import { push, link } from 'svelte-spa-router';
  import { t, tImmediate } from '../i18n/index.js';
  import { FilePlus, Trash2, Plus, Send, Globe, AlertTriangle } from '../lib/icons/index.js';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import SuccessBanner from '../components/SuccessBanner.svelte';
  import InfoHint from '../components/InfoHint.svelte';
  import NaturaIvaSelect from '../components/NaturaIvaSelect.svelte';
  import { missingNaturaLines, requiresNatura, defaultNaturaFor } from '../lib/natura.js';
  import { computeInvoiceTotals, submittedLineItems } from '../lib/totals.js';
  import { invoiceWarningNotices } from '../lib/warnings.js';
  import { todayCalendarDay } from '../lib/payment.js';
  import InvoiceNumberField from '../components/InvoiceNumberField.svelte';
  import { numberingPayload, numberingReady } from '../lib/numbering.js';
  import { issueViaDraft, IssueOutcomeUnknownError, emptyIssueState, issueKeyOf, acknowledgementFor } from '../lib/issue-via-draft.js';
  import { createRequestGeneration } from '../lib/request-generation.js';
  import { formatDateTime, formatEuro } from '../lib/labels.js';

  let loading = false;
  let error = '';
  let success = '';
  // See InvoiceCreate: a notice cancels the automatic redirect so it can be read.
  let warningNotices = [];
  let createdInvoiceId = '';

  // What the form remembers across clicks of «Emetti»: the draft it saved and
  // what it sent. A refused or interrupted attempt keeps it, so the next click
  // reuses that draft — or finds it already issued — instead of creating a
  // second document. See lib/issue-via-draft.js.
  let issueState = emptyIssueState();
  let keptDraftId = '';
  let alreadyIssued = null;
  // A promotion the server held back because a lookalike was issued in the
  // last 24 hours: the documents it named, until the operator answers.
  let possibleDuplicate = null;
  let duplicateCancelled = false;
  // The warning's token, set only by «Emetti comunque» for the one click it
  // answers, and only while the form is the one the warning was given for.
  let acknowledgeNext = null;

  // Every issue belongs to the company it was started for. A switch resets the
  // form's memory, and an answer that arrives afterwards is dropped — also
  // when the operator has come back to the first company in the meantime.
  const generation = createRequestGeneration();
  let issueCompanyId = $activeCompanyId;
  $: companySwitched($activeCompanyId);
  function companySwitched(companyId) {
    if (companyId === issueCompanyId) return;
    issueCompanyId = companyId;
    resetIssue();
  }
  function resetIssue() {
    generation.invalidate();
    issueState = emptyIssueState();
    keptDraftId = '';
    alreadyIssued = null;
    possibleDuplicate = null;
    duplicateCancelled = false;
    loading = false;
  }
  function issueAnyway() {
    acknowledgeNext = acknowledgementFor(possibleDuplicate, draftPayload(), promoteBase());
    handleSubmit();
  }
  // Any edit after the warning drops it with its token: the next «Emetti»
  // saves the edited draft and has it checked again.
  function formEdited() {
    if (possibleDuplicate) possibleDuplicate = null;
  }
  // The number travels only when typed by hand; otherwise the server assigns
  // the next one of the series at the moment of issue.
  function promoteBase() {
    return { invoiceDate, ...numberingPayload({ manual: manualNumber, invoiceNumber }) };
  }
  // Nothing was issued; the draft stays, and the next «Emetti» reuses it.
  function cancelIssue() {
    possibleDuplicate = null;
    duplicateCancelled = true;
    keptDraftId = issueState.draftId || '';
  }

  // Form state
  let tipoDocumento = 'TD17';
  // The number is assigned by the server when the document is issued;
  // `invoiceNumber` is only the override typed with «Indica il numero a mano».
  let manualNumber = false;
  let invoiceNumber = '';
  let invoiceDate = todayCalendarDay();
  let causale = '';

  // Compatibility input for the foreign supplier's original document. On
  // TD17/TD18 generation its number/date go to DatiFattureCollegate;
  // invoiceNumber/invoiceDate above remain ours.
  let fatturaPrincipaleNumero = '';
  let fatturaPrincipaleData = '';

  // The XSD makes both fields mandatory inside the block, so half of it is not
  // a partial reference, it is a document the backend rejects with a 400.
  $: fatturaPrincipaleIncomplete = (!!fatturaPrincipaleNumero.trim()) !== (!!fatturaPrincipaleData);

  // Foreign supplier — either picked from anagrafica or filled inline
  let supplierMode = 'inline'; // 'inline' or 'anagrafica'
  let selectedSupplier = null;
  let fornitore = {
    idPaese: 'DE',
    idCodice: '',
    denominazione: '',
    indirizzo: '',
    cap: '',
    comune: '',
    nazione: 'DE',
  };

  // Line items (always >= 1)
  let lineItems = [emptyLineItem()];

  function emptyLineItem() {
    return { descrizione: '', quantita: '1', prezzoUnitario: '', aliquotaIva: '22.00', natura: '', unitaMisura: 'NR' };
  }
  function addLineItem() { lineItems = [...lineItems, emptyLineItem()]; }
  function removeLineItem(i) {
    if (lineItems.length <= 1) return;
    lineItems = lineItems.filter((_, idx) => idx !== i);
  }

  function handleSupplierSelect(customer) {
    selectedSupplier = customer;
    if (customer) {
      fornitore = {
        idPaese: customer.idPaese || 'DE',
        idCodice: customer.partitaIva || '',
        denominazione: customer.ragioneSociale || `${customer.nome || ''} ${customer.cognome || ''}`.trim(),
        indirizzo: customer.sede?.indirizzo || '',
        cap: customer.sede?.cap || '',
        comune: customer.sede?.comune || '',
        nazione: customer.sede?.nazione || customer.idPaese || 'DE',
      };
    }
  }

  // Totals. The same computation the invoice editor and the backend use — see
  // lib/totals.js — so the preview and the transmitted document cannot differ.
  // The payload below is built from the very rows that were summed: a row with
  // no description is dropped from both, never counted in one and sent in the
  // other. The autofattura endpoint states no document-level esigibilità, so
  // the default the generator applies is the one summed here.
  $: submittedLines = submittedLineItems(lineItems);
  $: totals = computeInvoiceTotals(lineItems);

  // Lines that are zero-rated but still missing their Natura code.
  $: missingNatura = missingNaturaLines(lineItems);

  // docs/autofatture.md prescribes Italian VAT (typically 22%) on TD17/TD18, so
  // a zero-rated line here is a deliberate reverse-charge choice. When the
  // operator makes it, seed the matching Natura instead of leaving an empty
  // select. Fired only on the rate change, so a code the operator picks (or
  // clears) afterwards is never overwritten.
  function handleRateChange(i) {
    const item = lineItems[i];
    if (!item || !requiresNatura(item.aliquotaIva) || item.natura) return;
    const suggested = defaultNaturaFor(tipoDocumento);
    if (!suggested) return;
    item.natura = suggested;
    lineItems = lineItems;
  }

  /**
   * The self-invoice as a draft: everything but the number and the date, which
   * are given at the moment of issue. The payload below is built from the very
   * rows that were summed.
   */
  function draftPayload() {
    const payload = {
      tipoDocumento,
      totalAmount: totals.totaleText,
      causale: causale || null,
      lineItems: submittedLines,
      // Both halves or neither: the block is only meaningful complete, and the
      // guard in handleSubmit() has already stopped the half-filled case.
      fatturaPrincipale: fatturaPrincipaleNumero.trim() && fatturaPrincipaleData
        ? { numero: fatturaPrincipaleNumero.trim(), data: fatturaPrincipaleData }
        : null,
    };
    if (supplierMode === 'anagrafica' && selectedSupplier?.id) {
      payload.supplierId = selectedSupplier.id;
    } else {
      payload.fornitore = fornitore;
    }
    return payload;
  }

  async function handleSubmit() {
    error = '';
    success = '';
    warningNotices = [];
    createdInvoiceId = '';
    keptDraftId = '';
    alreadyIssued = null;
    possibleDuplicate = null;
    duplicateCancelled = false;
    const acknowledgement = acknowledgeNext;
    acknowledgeNext = null;
    if (!numberingReady({ manual: manualNumber, invoiceNumber })) { error = tImmediate('numbering.manualEmpty'); return; }
    if (!fornitore.denominazione) { error = tImmediate('autofatturaCreate.errors.supplierNameRequired'); return; }
    if (!fornitore.idCodice) { error = tImmediate('autofatturaCreate.errors.supplierIdCodiceRequired'); return; }
    if (lineItems.every(item => !item.descrizione)) { error = tImmediate('autofatturaCreate.errors.atLeastOneLine'); return; }
    if (missingNatura.length > 0) {
      error = tImmediate('naturaIva.missingOnLines', { lines: missingNatura.join(', ') });
      return;
    }

    if (fatturaPrincipaleIncomplete) {
      error = tImmediate('autofatturaCreate.errors.fatturaPrincipaleIncomplete');
      return;
    }

    if (!$activeCompany?.codiceDestinatario && !$activeCompany?.pecDestinatario) {
      error = tImmediate('autofatturaCreate.errors.companyDestinatarioMissing');
      return;
    }

    loading = true;
    const gen = generation.begin();
    const payload = draftPayload();
    const base = promoteBase();
    try {
      const outcome = await issueViaDraft({
        api: {
          createDraft: companyAutofattureApi.createDraft,
          updateDraft: companyInvoicesApi.updateDraft,
          deleteDraft: companyInvoicesApi.deleteDraft,
          promoteDraft: companyInvoicesApi.promoteDraft,
          getInvoice: companyInvoicesApi.get,
        },
        companyId: $activeCompanyId,
        state: issueState,
        payload,
        promoteBody: { ...base, ...(acknowledgement && { acknowledgeDuplicates: acknowledgement }) },
        // A self-invoice draft cannot be edited: a changed one is replaced.
        immutable: true,
      });
      if (!generation.isCurrent(gen)) return;
      issueState = outcome.state;
      if (outcome.outcome === 'possible_duplicate') {
        possibleDuplicate = {
          matches: outcome.matches,
          windowHours: outcome.windowHours ?? 24,
          token: outcome.duplicateCheckToken,
          formKey: issueKeyOf(payload, base),
        };
      } else if (outcome.outcome === 'issued') {
        const result = outcome.result;
        // The server's number, never the preview: another document of the
        // series may have been issued in between.
        success = tImmediate(result.awaitingManualSignature
          ? 'autofatturaCreate.successMessageAwaitingSignature'
          : 'autofatturaCreate.successMessage', { tipo: tipoDocumento, invoiceNumber: result.invoiceNumber, id: result.id });
        warningNotices = invoiceWarningNotices(result.warnings);
        createdInvoiceId = result.id;
        // Navigate to invoice detail after short delay — unless there is a
        // notice to read, which a redirect would take off the screen before
        // anybody had the chance.
        if (warningNotices.length === 0) {
          setTimeout(() => push(`/invoices/${result.id}`), 1500);
        }
      } else {
        alreadyIssued = { invoiceId: outcome.invoiceId, changesDiscarded: outcome.changesDiscarded };
        // Discarded edits are worth reading before leaving the form.
        if (!outcome.changesDiscarded) {
          setTimeout(() => push(`/invoices/${outcome.invoiceId}`), 1500);
        }
      }
    } catch (err) {
      if (!generation.isCurrent(gen)) return;
      if (err instanceof IssueOutcomeUnknownError) {
        // Nothing new was written; the next click checks the kept draft first.
        issueState = err.state;
        error = tImmediate('form.issue.outcomeUnknown');
      } else {
        if (err?.state) issueState = err.state;
        keptDraftId = err?.draftId || '';
        // Keep the Error object so ErrorBanner maps it to a friendly message;
        // validation paths above assign already-localized strings.
        error = err;
      }
    }
    loading = false;
  }

  const tipoCodes = ['TD17', 'TD18'];
  const ivaRates = ['22.00', '10.00', '5.00', '4.00', '0.00'];

  // ISO 3166-1 alpha-2 for typical EU/extra-EU partners — operator can edit free-text
  const commonCountries = ['DE', 'FR', 'ES', 'NL', 'BE', 'AT', 'PT', 'IE', 'SE', 'DK', 'FI', 'PL', 'CZ', 'GR', 'GB', 'CH', 'US', 'CN', 'JP', 'XX'];

  const inputCls = "input";
  const inputStyle = "";
  const labelCls = "block eyebrow mb-1";
  const labelStyle = "color: var(--color-text-muted);";
</script>

<Layout>
  <div class="max-w-5xl mx-auto">
    <div class="flex items-center gap-3 mb-2">
      <h1 class="section-title mb-0">{$t('autofatturaCreate.title')}</h1>
    </div>
    <p class="view-subtitle mt-2 mb-6">{$t('autofatturaCreate.subtitle')}</p>

    <!-- Discovery / explanation banner -->
    <div class="alert alert-accent mb-6">
      <AlertTriangle size={18} class="alert-mark mt-0.5" />
      <div class="text-xs space-y-2 measure" style="color: var(--color-text-body);">
        <p><strong>{$t('autofatturaCreate.help.whatTitle')}</strong> {$t('autofatturaCreate.help.whatBody')}</p>
        <p><strong>{$t('autofatturaCreate.help.whenTitle')}</strong> {$t('autofatturaCreate.help.whenBody')}</p>
        <p><strong>{$t('autofatturaCreate.help.flowTitle')}</strong> {$t('autofatturaCreate.help.flowBody')}</p>
      </div>
    </div>

    <ErrorBanner error={error} raw={typeof error === 'string'} />

    <!-- The server held the promotion back: a document just like this one was
         issued in the last 24 hours. Nothing is issued until the operator says. -->
    {#if possibleDuplicate}
      <div class="alert alert-accent mb-4" role="alert">
        <AlertTriangle size={18} class="alert-mark mt-0.5" />
        <div class="min-w-0 flex-1">
          <p class="text-sm measure" style="color: var(--color-text-body);">{$t('form.issue.possibleDuplicate', { ore: possibleDuplicate.windowHours })}</p>
          <ul class="mt-2 space-y-1 text-sm" style="color: var(--color-text-body);">
            {#each possibleDuplicate.matches as match (match.id)}
              <li>
                {$t('form.issue.possibleDuplicateMatch', { numero: match.invoiceNumber, emessoIl: formatDateTime(match.issuedAt), totale: formatEuro(match.totalAmount) })}
                <a href={`/invoices/${match.id}`} use:link class="ml-2 eyebrow text-action">{$t('invoiceWarnings.openInvoice')}</a>
              </li>
            {/each}
          </ul>
          <p class="text-xs measure mt-2" style="color: var(--color-text-body);">{$t('form.issue.possibleDuplicateQuestion')}</p>
          <div class="flex flex-wrap gap-2 mt-3">
            <button type="button" on:click={issueAnyway} disabled={loading} class="btn btn-primary btn-sm">{$t('form.issue.issueAnyway')}</button>
            <button type="button" on:click={cancelIssue} disabled={loading} class="btn btn-secondary btn-sm">{$t('form.issue.cancelIssue')}</button>
          </div>
        </div>
      </div>
    {/if}

    <!-- A refusal leaves nothing issued, and nothing lost: the data is in a
         draft, which the next click reuses instead of creating another. -->
    {#if keptDraftId}
      <div class="alert alert-accent mb-4" role="status">
        <AlertTriangle size={18} class="alert-mark mt-0.5" />
        <div class="min-w-0 flex-1">
          <p class="text-xs measure" style="color: var(--color-text-body);">{$t(duplicateCancelled ? 'form.issue.duplicateCancelled' : 'form.issue.draftKept')}</p>
          <a href={`/drafts/${keptDraftId}`} use:link class="inline-block mt-2 eyebrow text-action">
            {$t('form.issue.draftKeptLink')}
          </a>
        </div>
      </div>
    {/if}

    <!-- An earlier click had already issued this document. When the form has
         changed since, say so: those edits are not in what was sent. -->
    {#if alreadyIssued}
      <div class="alert alert-accent mb-4" role="status" aria-live="polite">
        <AlertTriangle size={18} class="alert-mark mt-0.5" />
        <div class="min-w-0 flex-1">
          {#if alreadyIssued.changesDiscarded}
            <p class="text-sm measure" style="color: var(--color-text-body);">{$t('form.issue.alreadyIssuedChangesDiscarded')}</p>
          {:else}
            <p class="text-sm measure" style="color: var(--color-text-body);">{$t('form.issue.alreadyIssued')}</p>
          {/if}
          <a href={`/invoices/${alreadyIssued.invoiceId}`} use:link class="inline-block mt-3 eyebrow text-action">
            {$t('invoiceWarnings.openInvoice')}
          </a>
        </div>
      </div>
    {/if}
    <!-- role=status + no monospace: this confirmation fires immediately before
         an automatic redirect, so a screen-reader user would otherwise be moved
         to another page without ever being told the invoice was created. -->
    {#if success}
      <SuccessBanner message={success} />
    {/if}

    <!-- Not an error: the autofattura exists and has been queued. -->
    {#if warningNotices.length > 0}
      <div class="alert alert-accent mb-4" role="status" aria-live="polite">
        <AlertTriangle size={18} class="alert-mark mt-0.5" />
        <div class="min-w-0 flex-1">
          <p class="text-sm font-bold" style="color: var(--color-text-heading);">{$t('invoiceWarnings.title')}</p>
          {#each warningNotices as notice}
            <p class="mt-2 text-xs measure" style="color: var(--color-text-body);">{notice.text}</p>
            {#if notice.detail}
              <p class="mt-1 text-xs measure" style="color: var(--color-text-muted);">{notice.detail}</p>
            {/if}
          {/each}
          {#if createdInvoiceId}
            <a href={`/invoices/${createdInvoiceId}`} use:link class="inline-block mt-3 eyebrow text-action">
              {$t('invoiceWarnings.openInvoice')}
            </a>
          {/if}
        </div>
      </div>
    {/if}

    <form on:submit|preventDefault={handleSubmit} on:input={formEdited} on:change={formEdited}>
      <!-- Section: Tipo documento -->
      <div class="card p-6 mb-4">
        <h2 class="eyebrow eyebrow-lg mb-4" style="color: var(--color-primary);">{$t('autofatturaCreate.sections.docType')}</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
          <div>
            <span id="{fid}-1" class={labelCls} style={labelStyle}>{$t('autofatturaCreate.fields.tipoDocumento')}</span>
            <select aria-labelledby="{fid}-1" bind:value={tipoDocumento} class={inputCls} style={inputStyle}>
              {#each tipoCodes as code}
                <option value={code}>{code} — {$t(`autofatturaCreate.tipiDocumento.${code}.short`)}</option>
              {/each}
            </select>
            <p class="mt-1 text-xs font-mono" style="color: var(--color-text-muted);">
              {$t(`autofatturaCreate.tipiDocumento.${tipoDocumento}.description`)}
            </p>
          </div>
          <div>
            <InvoiceNumberField companyId={$activeCompanyId} {tipoDocumento} {invoiceDate} bind:manual={manualNumber} bind:invoiceNumber />
          </div>
          <div>
            <span id="{fid}-2" class={labelCls} style={labelStyle}>{$t('autofatturaCreate.fields.date')}</span>
            <input aria-labelledby="{fid}-2" bind:value={invoiceDate} type="date" class={inputCls} style={inputStyle} />
          </div>
        </div>
        <!-- The single easiest mistake on this form is typing the supplier's
             invoice number here. Say whose numbering this is, right under the
             two fields, and point at where the supplier's own document goes. -->
        <p class="mb-4 text-xs measure" style="color: var(--color-text-muted);">{$t('autofatturaCreate.fields.ownDocumentNote')}</p>
        <div>
          <span id="{fid}-3" class={labelCls} style={labelStyle}>{$t('autofatturaCreate.fields.causale')}</span>
          <input aria-labelledby="{fid}-3" bind:value={causale} class={inputCls} style={inputStyle} placeholder={$t('autofatturaCreate.placeholders.causale')} />
        </div>
      </div>

      <!-- Section: Fornitore estero -->
      <div class="card p-6 mb-4">
        <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 class="eyebrow eyebrow-lg inline-flex items-center gap-1" style="color: var(--color-primary);">{$t('autofatturaCreate.sections.supplier')}<InfoHint term="cedenteCessionario" /></h2>
          <div class="segmented" role="group">
            <button type="button" on:click={() => { supplierMode = 'inline'; }} aria-pressed={supplierMode === 'inline'}>
              {$t('autofatturaCreate.supplierMode.inline')}
            </button>
            <button type="button" on:click={() => { supplierMode = 'anagrafica'; }} aria-pressed={supplierMode === 'anagrafica'}>
              {$t('autofatturaCreate.supplierMode.anagrafica')}
            </button>
          </div>
        </div>

        {#if supplierMode === 'anagrafica'}
          <div class="mb-4">
            <span class={labelCls} style={labelStyle}>{$t('autofatturaCreate.fields.supplierAutocomplete')}</span>
            <!-- An autofattura documents a purchase, so only fornitori (and
                 dual-role records) belong in this picker. -->
            <CustomerAutocomplete kind="fornitore" bind:value={selectedSupplier} onSelect={handleSupplierSelect} />
            <p class="mt-1 text-xs font-mono" style="color: var(--color-text-muted);">{$t('autofatturaCreate.fields.supplierAutocompleteHint')}</p>
          </div>
        {/if}

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <span id="{fid}-4" class={labelCls} style={labelStyle}>{$t('autofatturaCreate.fields.idPaese')}</span>
            <select aria-labelledby="{fid}-4" bind:value={fornitore.idPaese} class={inputCls} style={inputStyle}>
              {#each commonCountries as c}<option value={c}>{c}</option>{/each}
            </select>
          </div>
          <div class="md:col-span-2">
            <span id="{fid}-5" class={labelCls} style={labelStyle}>{$t('autofatturaCreate.fields.idCodice')}</span>
            <input aria-labelledby="{fid}-5" bind:value={fornitore.idCodice} class={inputCls} style={inputStyle} placeholder={$t('autofatturaCreate.placeholders.idCodice')} />
          </div>
          <div class="md:col-span-3">
            <span id="{fid}-6" class={labelCls} style={labelStyle}>{$t('autofatturaCreate.fields.denominazione')}</span>
            <input aria-labelledby="{fid}-6" bind:value={fornitore.denominazione} class={inputCls} style={inputStyle} />
          </div>
          <div class="md:col-span-2">
            <span id="{fid}-7" class={labelCls} style={labelStyle}>{$t('autofatturaCreate.fields.address')}</span>
            <input aria-labelledby="{fid}-7" bind:value={fornitore.indirizzo} class={inputCls} style={inputStyle} />
          </div>
          <div>
            <span id="{fid}-8" class={labelCls} style={labelStyle}>{$t('autofatturaCreate.fields.cap')}</span>
            <input aria-labelledby="{fid}-8" bind:value={fornitore.cap} class={inputCls} style={inputStyle} />
          </div>
          <div class="md:col-span-2">
            <span id="{fid}-9" class={labelCls} style={labelStyle}>{$t('autofatturaCreate.fields.city')}</span>
            <input aria-labelledby="{fid}-9" bind:value={fornitore.comune} class={inputCls} style={inputStyle} />
          </div>
          <div>
            <span id="{fid}-10" class={labelCls} style={labelStyle}>{$t('autofatturaCreate.fields.nation')}</span>
            <input aria-labelledby="{fid}-10" bind:value={fornitore.nazione} maxlength="2" class={inputCls} style={inputStyle} />
          </div>
        </div>

        <!-- FatturaPrincipale. Kept inside the supplier card, not next to our
             own number and date, so that "whose document is this?" is answered
             by where the fields are before it is answered by their labels. -->
        <div class="mt-6 border-t-w pt-4" style="border-color: var(--color-tint-medium);">
          <h3 class="eyebrow inline-flex items-center gap-1 mb-2" style="color: var(--color-primary);">
            {$t('autofatturaCreate.sections.fatturaPrincipale')}<InfoHint term="fatturaPrincipale" />
          </h3>
          <p class="text-xs measure mb-3" style="color: var(--color-text-muted);">{$t('autofatturaCreate.fatturaPrincipale.description')}</p>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class={labelCls} style={labelStyle} for="fattura-principale-numero">{$t('autofatturaCreate.fatturaPrincipale.numero')}</label>
              <input id="fattura-principale-numero" bind:value={fatturaPrincipaleNumero} maxlength="20"
                class={inputCls} style={inputStyle}
                aria-describedby="fattura-principale-help"
                placeholder={$t('autofatturaCreate.placeholders.fatturaPrincipaleNumero')} />
            </div>
            <div>
              <label class={labelCls} style={labelStyle} for="fattura-principale-data">{$t('autofatturaCreate.fatturaPrincipale.data')}</label>
              <input id="fattura-principale-data" bind:value={fatturaPrincipaleData} type="date"
                class={inputCls} style={inputStyle} aria-describedby="fattura-principale-help" />
            </div>
          </div>

          <p id="fattura-principale-help" class="mt-2 text-xs italic" style="color: var(--color-text-muted);">{$t('autofatturaCreate.fatturaPrincipale.bothRequired')}</p>

          {#if fatturaPrincipaleIncomplete}
            <p role="alert" class="mt-2 text-xs font-bold text-danger">{$t('autofatturaCreate.errors.fatturaPrincipaleIncomplete')}</p>
          {/if}
        </div>
      </div>

      <!-- Section: Line items -->
      <div class="card p-6 mb-4">
        <h2 class="eyebrow eyebrow-lg mb-4" style="color: var(--color-primary);">{$t('autofatturaCreate.sections.lineItems')}</h2>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b-w" style="border-color: var(--color-tint-medium);">
                <th class="text-left py-2 px-1 text-xs font-bold uppercase" style="color: var(--color-text-muted);">#</th>
                <th class="text-left py-2 px-1 text-xs font-bold uppercase" style="color: var(--color-text-muted);">{$t('autofatturaCreate.fields.description')}</th>
                <th class="text-right py-2 px-1 text-xs font-bold uppercase w-20" style="color: var(--color-text-muted);">{$t('autofatturaCreate.fields.quantity')}</th>
                <th class="text-right py-2 px-1 text-xs font-bold uppercase w-28" style="color: var(--color-text-muted);">{$t('autofatturaCreate.fields.price')}</th>
                <th class="text-right py-2 px-1 text-xs font-bold uppercase w-20" style="color: var(--color-text-muted);">{$t('autofatturaCreate.fields.vatPercent')}</th>
                <th class="text-right py-2 px-1 text-xs font-bold uppercase w-16" style="color: var(--color-text-muted);">{$t('autofatturaCreate.fields.unitMeasure')}</th>
                <th class="text-right py-2 px-1 text-xs font-bold uppercase w-28" style="color: var(--color-text-muted);">{$t('autofatturaCreate.fields.total')}</th>
                <th class="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {#each lineItems as item, i}
                <tr class="border-b" style="border-color: var(--color-tint-soft);">
                  <td class="py-1 px-1 font-mono text-xs" style="color: var(--color-text-muted);">{i + 1}</td>
                  <td class="py-1 px-1">
                    <input aria-label={$t('invoiceCreate.lineItemField', { campo: $t('autofatturaCreate.fields.description'), riga: i + 1 })} bind:value={item.descrizione} class="input input-compact" />
                  </td>
                  <td class="py-1 px-1">
                    <input aria-label={$t('invoiceCreate.lineItemField', { campo: $t('autofatturaCreate.fields.quantity'), riga: i + 1 })} bind:value={item.quantita} type="number" step="0.01" min="0" class="input input-compact text-right tabular" />
                  </td>
                  <td class="py-1 px-1">
                    <input aria-label={$t('invoiceCreate.lineItemField', { campo: $t('autofatturaCreate.fields.price'), riga: i + 1 })} bind:value={item.prezzoUnitario} type="number" step="0.01" min="0" class="input input-compact text-right tabular" />
                  </td>
                  <td class="py-1 px-1">
                    <select aria-label={$t('invoiceCreate.lineItemField', { campo: $t('autofatturaCreate.fields.vatPercent'), riga: i + 1 })} bind:value={item.aliquotaIva} on:change={() => handleRateChange(i)} class="input input-compact text-right tabular">
                      {#each ivaRates as rate}<option value={rate}>{rate}</option>{/each}
                    </select>
                  </td>
                  <td class="py-1 px-1">
                    <input aria-label={$t('invoiceCreate.lineItemField', { campo: $t('autofatturaCreate.fields.unitMeasure'), riga: i + 1 })} bind:value={item.unitaMisura} class="input input-compact" />
                  </td>
                  <td class="py-1 px-1 text-right font-mono text-sm" style="color: var(--color-text-body);">
                    {totals.lineTotalTexts[i] || '0.00'}
                  </td>
                  <td class="py-1 px-1">
                    {#if lineItems.length > 1}
                      <button type="button" on:click={() => removeLineItem(i)} class="p-1 hover:text-danger" style="color: var(--color-text-muted);">
                        <Trash2 size={14} />
                      </button>
                    {/if}
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
        <button type="button" on:click={addLineItem} class="mt-3 flex items-center gap-1 eyebrow hover:text-action transition-colors" style="color: var(--color-text-muted);">
          <Plus size={14} /> {$t('autofatturaCreate.fields.addRow')}
        </button>

        <!-- Natura IVA: required by law on any 0%-VAT line (reverse charge → N6.x). -->
        {#each lineItems as item, i}
          {#if requiresNatura(item.aliquotaIva)}
            <div class="mt-4 border-t pt-3" style="border-color: var(--color-tint-soft);">
              <span class="eyebrow" style="color: var(--color-text-muted);">{$t('autofatturaCreate.fields.naturaLineLabel', { n: i + 1 })}</span>
              <div class="mt-1">
                <NaturaIvaSelect bind:value={item.natura} vatRate={item.aliquotaIva} />
              </div>
              <p class="mt-2 text-xs italic" style="color: var(--color-text-muted);">
                {$t('autofatturaCreate.fields.naturaZeroRateNote')}
              </p>
            </div>
          {/if}
        {/each}
      </div>

      <!-- Section: Summary -->
      <div class="card p-6 mb-4">
        <h2 class="eyebrow eyebrow-lg mb-4" style="color: var(--color-primary);">{$t('autofatturaCreate.sections.summary')}</h2>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div>
            <span class="text-xs" style="color: var(--color-text-muted);">{$t('autofatturaCreate.summary.type')}</span>
            <p class="font-mono font-bold" style="color: var(--color-text-body);">{tipoDocumento}</p>
          </div>
          <div>
            <span class="text-xs" style="color: var(--color-text-muted);">{$t('autofatturaCreate.summary.number')}</span>
            <p class="font-mono font-bold" style="color: var(--color-text-body);">{manualNumber ? (invoiceNumber || '-') : $t('numbering.summaryAuto')}</p>
          </div>
          {#if fatturaPrincipaleNumero.trim() && fatturaPrincipaleData}
            <div>
              <span class="text-xs" style="color: var(--color-text-muted);">{$t('autofatturaCreate.summary.fatturaPrincipale')}</span>
              <p class="font-mono font-bold truncate" style="color: var(--color-text-body);">{fatturaPrincipaleNumero} · {fatturaPrincipaleData}</p>
            </div>
          {/if}
          <div>
            <span class="text-xs" style="color: var(--color-text-muted);">{$t('autofatturaCreate.summary.supplier')}</span>
            <p class="font-mono font-bold truncate" style="color: var(--color-text-body);">{fornitore.denominazione || '-'}</p>
          </div>
          <div>
            <span class="text-xs" style="color: var(--color-text-muted);">{$t('autofatturaCreate.summary.destinatario')}</span>
            <p class="font-mono font-bold" style="color: var(--color-text-body);">{$activeCompany?.codiceDestinatario || $activeCompany?.pecDestinatario || '-'}</p>
          </div>
        </div>
        <div class="border-t-w pt-4" style="border-color: var(--color-tint-medium);">
          <div class="space-y-1">
            {#each totals.groups as group}
              <div class="flex justify-between text-sm font-mono">
                <span style="color: var(--color-text-muted);">{$t('autofatturaCreate.summary.taxable', { rate: group.aliquota })}</span>
                <span style="color: var(--color-text-body);">{group.imponibileText} &euro;</span>
              </div>
              <div class="flex justify-between text-sm font-mono">
                <span style="color: var(--color-text-muted);">{$t('autofatturaCreate.summary.vat', { rate: group.aliquota })}</span>
                <span style="color: var(--color-text-body);">{group.impostaText} &euro;</span>
              </div>
            {/each}
          </div>
          <div class="flex justify-between text-lg font-bold font-mono border-t-w pt-2 mt-2" style="border-color: var(--color-tint-medium);">
            <span style="color: var(--color-text-heading);">{$t('autofatturaCreate.summary.total')}</span>
            <span class="text-action">{totals.totaleText} &euro;</span>
          </div>
        </div>
      </div>

      <div class="flex flex-col items-end gap-2">
        <button type="submit" disabled={loading}
          class="btn btn-primary gap-2">
          {#if loading}
            <span class="animate-pulse">{$t('app.sending')}</span>
          {:else}
            <Send size={18} />
            <span>{$t('autofatturaCreate.issue.button')}</span>
          {/if}
        </button>
        <p class="text-xs measure text-right" style="color: var(--color-text-muted);">{$t('autofatturaCreate.issue.explainer')}</p>
      </div>
    </form>
  </div>
</Layout>
